import React from "react"
import { Stack } from "@mui/material"
import { SnackbarMessage } from "notistack"
import Button from "../Button"
import Alert, { AlertProps } from "../Alert"
import { useStyles } from "./styles"

export type Props = Omit<AlertProps, "action"> & {
  message?: SnackbarMessage
  action?: Record<string, () => void>
  persist?: boolean
  id?: number | string
  dismissButton?: boolean | string
  onDismiss?: (id: number | string) => void
}

const StaticSnackbar: React.FC<Props> = ({
  size = "lg",
  variant = "dark",
  severity = "success",
  persist = true,
  action: snackbarActions,
  message,
  id, // ID won't be available on statick snackbar
  dismissButton = true,
  onDismiss,
  className,
  "aria-live": ariaLive
}) => {
  const { classes, cx } = useStyles()

  const generateActions = () => {
    const actions = snackbarActions
      ? Object.keys(snackbarActions).map(actionKey => ({
          name: actionKey,
          action: snackbarActions[actionKey]
        }))
      : []

    if (dismissButton && !persist) {
      actions.push({
        name: typeof dismissButton === "string" ? dismissButton : "Dismiss",
        action: () => onDismiss?.(id)
      })
    }

    return (
      <Stack flexDirection="row" gap={1}>
        {actions.map(({ name, action }) => (
          <Button
            size={size !== "sm" ? "sm" : "xs"}
            key={name}
            bold
            variant="text"
            color={severity ?? "info"}
            // a11y: the default blue ring is ~1:1 on the dark toast fills; white holds >=3.5:1 on all of them (SC 1.4.11)
            focusRing={variant === "dark" ? "onDark" : undefined}
            onClick={action}
          >
            {name}
          </Button>
        ))}
      </Stack>
    )
  }

  return (
    <Alert
      id={id}
      className={cx(className, classes.root, { [classes.static]: persist })}
      variant={variant}
      severity={severity}
      size={size}
      action={generateActions()}
      aria-live={ariaLive}
    >
      {message}
    </Alert>
  )
}

export default StaticSnackbar
